// SitesModal.tsx
/** @jsxImportSource theme-ui */
import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Input, Text, Spinner } from "theme-ui";
import { message } from "antd";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import apiInvocation from "../api/apiInvocation";
import { Site, SiteDTO } from "../api/siteService";
import Icon from "./common/Icon";

// Fix leaflet default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const selectedIcon = new L.Icon({
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    className: "marker-selected",
});

interface SitesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Tab = "list" | "map";

const emptyForm: SiteDTO = { name: "", longitude: 0, latitude: 0 };

// Listens for map clicks to place a new pin
const MapClickHandler: React.FC<{ onMapClick: (lat: number, lng: number) => void }> = ({ onMapClick }) => {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

const SitesModal: React.FC<SitesModalProps> = ({ isOpen, onClose }) => {
    const [sites, setSites] = useState<Site[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState<SiteDTO>(emptyForm);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>("list");
    const [pendingPin, setPendingPin] = useState<{ lat: number; lng: number } | null>(null);
    const csvInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            loadSites();
        }
    }, [isOpen]);

    const loadSites = async () => {
        setIsLoading(true);
        try {
            const data = await apiInvocation.getAllSites();
            setSites(data);
        } catch {
            message.error("Failed to load sites.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const created = await apiInvocation.createSite(formData);
            setSites((prev) => [...prev, created]);
            setIsAdding(false);
            setFormData(emptyForm);
            setPendingPin(null);
            message.success("Site added successfully.");
        } catch {
            message.error("Failed to add site.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (isSaving || editingId === null) return;
        setIsSaving(true);
        try {
            const updated = await apiInvocation.updateSite(editingId, formData);
            setSites((prev) => prev.map((s) => (s.id === editingId ? updated : s)));
            setEditingId(null);
            setFormData(emptyForm);
            setPendingPin(null);
            message.success("Site updated successfully.");
        } catch {
            message.error("Failed to update site.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await apiInvocation.deleteSite(id);
            setSites((prev) => prev.filter((s) => s.id !== id));
            if (editingId === id) cancelForm();
            message.success("Site deleted.");
        } catch {
            message.error("Failed to delete site.");
        }
    };

    const startEdit = (site: Site) => {
        setEditingId(site.id);
        setIsAdding(false);
        setPendingPin(null);
        setFormData({ name: site.name, longitude: site.longitude, latitude: site.latitude });
    };

    const cancelForm = () => {
        setEditingId(null);
        setIsAdding(false);
        setFormData(emptyForm);
        setPendingPin(null);
    };

    const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsImporting(true);
        try {
            const result = await apiInvocation.importSitesCsv(file);
            message.success(result.message);
            await loadSites();
        } catch {
            message.error("Failed to import CSV.");
        } finally {
            setIsImporting(false);
            if (csvInputRef.current) csvInputRef.current.value = "";
        }
    };

    // Map click — start adding a new site at that position
    const handleMapClick = (lat: number, lng: number) => {
        if (editingId !== null) return; // don't interrupt an active edit
        setPendingPin({ lat, lng });
        setIsAdding(true);
        setFormData({ name: "", latitude: lat, longitude: lng });
    };

    // Default map centre: centroid of all sites, or a default region if none loaded
    const mapCenter: [number, number] = sites.length > 0
        ? [
            sites.reduce((s, x) => s + x.latitude, 0) / sites.length,
            sites.reduce((s, x) => s + x.longitude, 0) / sites.length,
        ]
        : [32.7, -97.3];

    // Inline form shared between both tabs
    const renderForm = () => (
        <Box sx={{ mb: 3, p: 3, border: "1px solid #ccc", borderRadius: 2, flexShrink: 0 }}>
            <Text sx={{ fontWeight: "bold", mb: 3, display: "block", fontSize: [2, 3] }}>
                {isAdding ? "New Site" : "Edit Site"}
            </Text>
            <Box sx={{ display: "grid", gridTemplateColumns: ["1fr", "2fr 1fr 1fr"], gap: 3, mb: 3 }}>
                <Input
                    placeholder="Name"
                    value={formData.name}
                    onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                    autoFocus
                />
                <Input
                    placeholder="Latitude"
                    type="number"
                    step="any"
                    value={formData.latitude || ""}
                    onChange={(e) => setFormData((f) => ({ ...f, latitude: parseFloat(e.target.value) || 0 }))}
                />
                <Input
                    placeholder="Longitude"
                    type="number"
                    step="any"
                    value={formData.longitude || ""}
                    onChange={(e) => setFormData((f) => ({ ...f, longitude: parseFloat(e.target.value) || 0 }))}
                />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                    variant="primary"
                    onClick={isAdding ? handleAdd : handleUpdate}
                    disabled={isSaving || !formData.name.trim()}
                    sx={{ opacity: isSaving || !formData.name.trim() ? 0.7 : 1 }}
                >
                    {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button
                    onClick={cancelForm}
                    sx={{
                        backgroundColor: "transparent",
                        border: "1px solid #ccc",
                        color: "#888",
                        cursor: "pointer",
                        borderRadius: 4,
                        px: 3,
                        py: 2,
                        "&:hover": { color: "#e53e3e", borderColor: "#e53e3e", backgroundColor: "transparent" },
                    }}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );

    if (!isOpen) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                padding: [3, 4],
            }}
            onClick={onClose}
        >
            <Box
                sx={{
                    backgroundColor: "background",
                    borderRadius: 3,
                    padding: [4, 5],
                    width: ["calc(100% - 32px)", "90%", "800px"],
                    maxWidth: "800px",
                    maxHeight: "90vh",
                    position: "relative",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    flexDirection: "column",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <Button
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: [2, 3], right: [2, 3],
                        width: ["28px", "32px"], height: ["28px", "32px"],
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        color: "#666",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", padding: 0,
                        "&:hover": { backgroundColor: "#f0f0f0", transform: "scale(1.1)" },
                    }}
                >
                    <Text sx={{ fontSize: ["14px", "16px"], fontWeight: "bold", lineHeight: 1 }}>X</Text>
                </Button>

                <Text sx={{ fontSize: [5, 6], fontWeight: "medium", mb: 3, pr: 4, textAlign: "center", color: "text" }}>
                    Manage Sites
                </Text>

                {/* Tabs */}
                <Box sx={{ display: "flex", borderBottom: "2px solid #eee", mb: 3, flexShrink: 0 }}>
                    {(["list", "map"] as Tab[]).map((tab) => (
                        <Box
                            key={tab}
                            onClick={() => { cancelForm(); setActiveTab(tab); }}
                            sx={{
                                px: 4, py: 2,
                                cursor: "pointer",
                                fontWeight: activeTab === tab ? "bold" : "normal",
                                borderBottom: activeTab === tab ? "2px solid" : "2px solid transparent",
                                borderColor: activeTab === tab ? "primary" : "transparent",
                                mb: "-2px",
                                fontSize: [1, 2],
                                color: activeTab === tab ? "primary" : "secondaryText",
                                userSelect: "none",
                            }}
                        >
                            {tab === "list" ? "List" : "Map"}
                        </Box>
                    ))}
                </Box>

                {/* ── LIST TAB ── */}
                {activeTab === "list" && (
                    <>
                        {!isAdding && editingId === null && (
                            <Box sx={{ mb: 3, display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap", flexShrink: 0 }}>
                                <Button
                                    variant="primary"
                                    onClick={() => { setIsAdding(true); setFormData(emptyForm); }}
                                    sx={{ fontSize: [1, 2] }}
                                >
                                    + Add Site
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => csvInputRef.current?.click()}
                                    disabled={isImporting}
                                    sx={{ fontSize: [1, 2], opacity: isImporting ? 0.7 : 1 }}
                                >
                                    {isImporting ? "Importing..." : "Import CSV"}
                                </Button>
                                <input
                                    ref={csvInputRef}
                                    type="file"
                                    accept=".csv"
                                    style={{ display: "none" }}
                                    onChange={handleCsvImport}
                                />
                            </Box>
                        )}

                        {(isAdding || editingId !== null) && renderForm()}

                        <Text sx={{ fontSize: 0, color: "secondaryText", mb: 2, flexShrink: 0 }}>
                            CSV format: Name, Latitude, Longitude (with header row)
                        </Text>

                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <Spinner size={24} sx={{ color: "primaryText" }} />
                            </Box>
                        ) : (
                            <Box sx={{ overflowY: "auto", flex: 1 }}>
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateColumns: ["2fr 1fr 1fr 64px", "2fr 1fr 1fr 80px"],
                                    fontWeight: "bold",
                                    pb: 2,
                                    borderBottom: "2px solid #eee",
                                    mb: 2,
                                    fontSize: [1, 2],
                                    position: "sticky",
                                    top: 0,
                                    backgroundColor: "background",
                                }}>
                                    <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>Name</Text>
                                    <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>Lat</Text>
                                    <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>Lon</Text>
                                    <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}></Text>
                                </Box>
                                {sites.map((site) => (
                                    <Box
                                        key={site.id}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: ["2fr 1fr 1fr 64px", "2fr 1fr 1fr 80px"],
                                            alignItems: "center",
                                            py: 2,
                                            borderBottom: "1px solid #f0f0f0",
                                            fontSize: [1, 2],
                                            backgroundColor: editingId === site.id ? "#f0f7ff" : "transparent",
                                            "&:hover": { backgroundColor: editingId === site.id ? "#f0f7ff" : "#fafafa" },
                                        }}
                                    >
                                        <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 2 }}>
                                            {site.name}
                                        </Text>
                                        <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>{site.latitude.toFixed(4)}</Text>
                                        <Text sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>{site.longitude.toFixed(4)}</Text>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                onClick={() => startEdit(site)}
                                                variant="primary"
                                                sx={{ p: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                            >
                                                <Icon name="pencil" size={14} color="white" />
                                            </Button>
                                            <Button
                                                onClick={() => handleDelete(site.id)}
                                                sx={{
                                                    p: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                    backgroundColor: "#e53e3e", color: "white",
                                                    border: "none", borderRadius: 4,
                                                }}
                                            >
                                                <Icon name="bin" size={14} color="white" />
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                                {sites.length === 0 && (
                                    <Text sx={{ color: "secondaryText", textAlign: "center", py: 6, display: "block" }}>
                                        No sites yet. Add one manually or import a CSV file.
                                    </Text>
                                )}
                            </Box>
                        )}
                    </>
                )}

                {/* ── MAP TAB ── */}
                {activeTab === "map" && (
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                        {(isAdding || editingId !== null) && renderForm()}

                        {!isAdding && editingId === null && (
                            <Text sx={{ fontSize: 0, color: "secondaryText", mb: 2, flexShrink: 0 }}>
                                Click a marker to edit or delete · Click anywhere on the map to add a new site
                            </Text>
                        )}

                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <Spinner size={24} sx={{ color: "primaryText" }} />
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: "350px", borderRadius: 2, overflow: "hidden" }}>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={sites.length > 0 ? 10 : 6}
                                    style={{ height: "100%", width: "100%", minHeight: "350px" }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <MapClickHandler onMapClick={handleMapClick} />

                                    {/* Existing site markers */}
                                    {sites.map((site) => (
                                        <Marker
                                            key={site.id}
                                            position={[site.latitude, site.longitude]}
                                            icon={editingId === site.id ? selectedIcon : new L.Icon.Default()}
                                        >
                                            <Popup>
                                                <div style={{ minWidth: "160px" }}>
                                                    <strong style={{ display: "block", marginBottom: "4px" }}>
                                                        {site.name}
                                                    </strong>
                                                    <span style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "8px" }}>
                                                        {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                                                    </span>
                                                    <div style={{ display: "flex", gap: "6px" }}>
                                                        <button
                                                            onClick={() => startEdit(site)}
                                                            style={{
                                                                padding: "4px 10px", cursor: "pointer",
                                                                backgroundColor: "#3b82f6", color: "white",
                                                                border: "none", borderRadius: "4px", fontSize: "14px",
                                                            }}
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(site.id)}
                                                            style={{
                                                                padding: "4px 10px", cursor: "pointer",
                                                                backgroundColor: "#e53e3e", color: "white",
                                                                border: "none", borderRadius: "4px", fontSize: "14px",
                                                            }}
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}

                                    {/* Pending new pin (placed but not yet saved) */}
                                    {pendingPin && (
                                        <Marker position={[pendingPin.lat, pendingPin.lng]} />
                                    )}
                                </MapContainer>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SitesModal;
